import React from 'react';
import Icon from 'components/AppIcon';

const StandardProcessForm = ({ formData, errors, onInputChange, isValidating }) => {
  const handleWeightIncrement = (increment) => {
    const currentWeight = parseFloat(formData.materialWeight) || 0;
    const newWeight = Math.max(0, currentWeight + increment);
    onInputChange('materialWeight', newWeight.toString());
  };

  const handleUnitsIncrement = (increment) => {
    const currentUnits = parseFloat(formData.units) || 0;
    const newUnits = Math.max(0, currentUnits + increment);
    onInputChange('units', newUnits.toString());
  };

  const inputFields = [
    {
      name: 'materialName',
      label: 'Material',
      type: 'text',
      placeholder: 'e.g., Cotton Tees, Polyester Blend, Denim',
      icon: 'Package',
      helpText: 'Specify the type of textile material for processing'
    },
    {
      name: 'units',
      label: 'Units',
      type: 'number',
      placeholder: '0',
      icon: 'Hash',
      helpText: 'Total number of units to be processed',
      min: '0',
      step: '1',
      hasIncrementControls: true
    },
    {
      name: 'materialWeight',
      label: 'Material Weight (LBS)',
      type: 'number',
      placeholder: '0.00',
      icon: 'Weight',
      helpText: 'Total weight of material in pounds',
      min: '0',
      step: '0.01',
      hasIncrementControls: true
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
          Standard Process Inputs
        </h3>
        <p className="text-sm text-text-secondary">
          Basic material specifications for standard textile processing
        </p>
      </div>

      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        {inputFields.map((field) => (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={field.name}
              className="block text-sm font-body-medium text-text-primary"
            >
              <div className="flex items-center space-x-2">
                <Icon name={field.icon} size={16} className="text-primary" />
                <span>{field.label}</span>
                <span className="text-error">*</span>
              </div>
            </label>

            <div className="relative">
              {field.hasIncrementControls ? (
                <div className="relative">
                  <input
                    id={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={(e) => onInputChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    min={field.min}
                    step={field.step}
                    className={`block w-full pr-20 pl-4 py-3 border rounded-lg text-sm placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      errors[field.name]
                        ? 'border-error bg-error-50 text-error' :'border-border bg-surface text-text-primary hover:border-primary'
                    }`}
                    aria-describedby={`${field.name}-help ${field.name}-error`}
                    aria-invalid={!!errors[field.name]}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      type="button"
                      onClick={() => field.name === 'materialWeight' ? handleWeightIncrement(-1) : handleUnitsIncrement(-1)}
                      className="px-2 py-1 text-text-secondary hover:text-primary focus:outline-none focus:text-primary transition-colors duration-150"
                      aria-label={`Decrease ${field.label.toLowerCase()}`}
                    >
                      <Icon name="Minus" size={16} />
                    </button>
                    <div className="w-px h-6 bg-border mx-1"></div>
                    <button
                      type="button"
                      onClick={() => field.name === 'materialWeight' ? handleWeightIncrement(1) : handleUnitsIncrement(1)}
                      className="px-2 py-1 text-text-secondary hover:text-primary focus:outline-none focus:text-primary transition-colors duration-150"
                      aria-label={`Increase ${field.label.toLowerCase()}`}
                    >
                      <Icon name="Plus" size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <input
                  id={field.name}
                  type={field.type}
                  value={formData[field.name]}
                  onChange={(e) => onInputChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  min={field.min}
                  step={field.step}
                  className={`block w-full px-4 py-3 border rounded-lg text-sm placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors[field.name]
                      ? 'border-error bg-error-50 text-error' :'border-border bg-surface text-text-primary hover:border-primary'
                  }`}
                  aria-describedby={`${field.name}-help ${field.name}-error`}
                  aria-invalid={!!errors[field.name]}
                />
              )}

              {/* Loading indicator for validation */}
              {isValidating && formData[field.name] && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <div className="animate-spin">
                    <Icon name="Loader2" size={16} className="text-primary" />
                  </div>
                </div>
              )}
            </div>

            {/* Help Text */}
            <p id={`${field.name}-help`} className="text-xs text-text-tertiary">
              {field.helpText}
            </p>

            {/* Error Message */}
            {errors[field.name] && (
              <div
                id={`${field.name}-error`}
                className="flex items-center space-x-2 text-sm text-error"
                role="alert"
              >
                <Icon name="AlertCircle" size={16} />
                <span>{errors[field.name]}</span>
              </div>
            )}
          </div>
        ))}
      </form>
    </div>
  );
};

export default StandardProcessForm;
